import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ProductInfoTable from '../Tables/ProductInfoTable';
import AverageCostingTable from '../Tables/AverageCostingTable';
import DetailedCostingTable from '../Tables/DetailedCostingTable';
import TestingTable from '../Tables/TestingTable';
import AdditionalTable from '../Tables/AdditionalTable';
import GrandTotal from '../Tables/GrandTotal';
import LandedInformationTable from '../Tables/LandedInformationTable';
import QuantitiesTable from '../Tables/QuantitiesTable';
import { useProductCostingState } from '../ProductCostingContext';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import AdditionalComponentTable from '../Tables/AdditionalComponentTable';
import SpecificationRequirementsTable from '../Tables/SpecificationRequirementsTable';
import CostingCardShell from '../Tables/CostingCardShell';
import type { CostingCardControls } from '../Tables/CostingCardShell';
import type { DistributionCentre } from '../types';

/** The redux slices this section reads. */
interface ProductCostingRootState {
  Users: { logInRights?: unknown };
  BOMConfigDetails: { productDetails?: { brandId?: { _id?: string } | string; subBrandId?: { _id?: string } | string } };
  UserDetails: { isSupplierLoggedIn?: boolean };
}

/** The costing detail body: every grid, laid out in the two-column costing grid. */
function ProductCosting(): React.JSX.Element {
  const { t } = useTranslation('common');
  /* UserRights.js declares `CheckActionPermission(rights, section, module = null, action = null)`,
     so TypeScript infers the last two parameters as `null` from their defaults. The cast restores
     the signature the function actually accepts — same shim as ProductCostingContext. */
  const { CheckActionPermission } = useComponent() as {
    CheckActionPermission: (rights: unknown, section: string, module?: string | null, action?: string | null) => boolean;
  };
  const { productCostingstate, detailedCostingState, filteredDistributionCentres, landedInfoStates } = useProductCostingState();
  const {
    Users: { logInRights },
  } = useSelector((prevState: ProductCostingRootState) => prevState);
  const {
    BOMConfigDetails: { productDetails },
    UserDetails: { isSupplierLoggedIn },
  } = useSelector((prevState: ProductCostingRootState) => prevState);

  const {
    averageCosting: { averageCostingActive },
    productTemplate,
    isGlobalUrl,
  } = productCostingstate;
  const {
    detailedCosting: { detailedCostingActive },
  } = detailedCostingState;
  const CoastingAddEditRights = CheckActionPermission(logInRights, 'products', 'costing', 'addEdit');

  const brandId = (productDetails?.brandId as { _id?: string } | undefined)?._id || productDetails?.brandId;
  const subBrandId = (productDetails?.subBrandId as { _id?: string } | undefined)?._id || productDetails?.subBrandId;
  const hasBrandOrSubBrand = !!(brandId || subBrandId);

  const dcList: DistributionCentre[] = landedInfoStates.length > 0 ? landedInfoStates.map((s) => s.distributionCentre) : filteredDistributionCentres;
  const renderLandedTables = (fullView: boolean, controls: CostingCardControls): React.ReactNode => {
    if (!hasBrandOrSubBrand || dcList.length === 0) {
      return <LandedInformationTable dcIndex={-1} showNoBrandWarning fullView={fullView} headerControls={controls} />;
    }
    const visibleList = fullView ? dcList : dcList.slice(0, 1);
    return visibleList.map((disCenter, i) => (
      <LandedInformationTable
        key={disCenter?.distributionName || i}
        dcIndex={i}
        distributionCentre={disCenter}
        fullView={fullView}
        headerControls={i === 0 ? controls : undefined}
      />
    ));
  };
  return (
    <div className="pt-4 landed-open">
      <ProductInfoTable CoastingAddEditRights={CoastingAddEditRights} />
      <div className="row costing-2col-grid form-row">
        <div className="col-lg-12 col-xl-6 costing-col-left">
          {averageCostingActive && <AverageCostingTable />}
          {!averageCostingActive && detailedCostingActive && <QuantitiesTable />}
        </div>
        <div className="col-lg-12 col-xl-6">
          {(productTemplate?.isSetUpCostActive as boolean | undefined) && <AdditionalTable />}
          {(productTemplate?.isAdditionalComponentActive as boolean | undefined) && <AdditionalComponentTable />}
        </div>
      </div>
      {/* Gated on the costing's embedded template snapshot, exactly like the two cards above. A
          costing created before BM-9899 has no such flag in its snapshot, so the section stays hidden
          until the template is re-applied or Refresh re-syncs it. */}
      {productTemplate?.isSpecificationRequirementActive === true && <SpecificationRequirementsTable />}
      {detailedCostingActive && <DetailedCostingTable />}
      <TestingTable />
      <GrandTotal />
      {!isSupplierLoggedIn && !isGlobalUrl && (
        <CostingCardShell floating renderOwnHeader title={t('DEVLOPMENT_ORDER.COSTING_DETAILS.LANDED_INFORMATION')}>
          {(fullView, controls) => renderLandedTables(fullView, controls)}
        </CostingCardShell>
      )}
    </div>
  );
}

export default ProductCosting;
